import { FunctionComponent, useCallback, memo } from 'react';
import { IntlShape } from 'react-intl';
import { useFormatMessage } from '../../i18n-review/client/Msg';
import { List } from '@phosphor-icons/react';
import { Icon } from '../../design-system/icons/Icon';
import { trackEvent } from '../../utils/analytics/analytics';
import { usePageContext } from 'vike-react/usePageContext';
import { AutocompleteHandle } from '../Autocomplete';
import Avatar from '../../design-system/Avatar';
import { SearchNavbar } from './SearchNavbar';
import { MyJobsNavEntry } from '../navigation/MyJobsNavEntry';
import { TopFilters } from '../filters/TopFilters';
import { useSearchFilters, useSearchQuery } from '../../contexts/SearchContext';
import { useFacets } from '../../contexts/FacetsContext';
import { useEffectiveUserProfilePhoto } from '../../hooks/useEffectiveUserProfilePhoto';
import { convertUrlToLanguage } from '../../utils/search-router/url-handling';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { LANG_FLAGS } from '../navigation/LanguageFlags';
import {
  CORE_CONTROL_GHOST,
  CORE_HEADER_STICKY_SRP,
  CORE_NAV_ROW_BORDER,
} from '../../constants/corePublicTheme';

interface DesktopFilterCategoriesProps {
  intl: IntlShape;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  isAllFiltersModalOpen?: boolean;
  setIsAllFiltersModalOpen?: (open: boolean) => void;
  /** Which rail section AllFiltersModal is currently showing (null while closed), so the
   * Location button only highlights when that's genuinely the tab in view. */
  allFiltersModalSection?: string | null;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: any;
  closeAutocomplete?: () => void;
  autocompleteRef?: React.RefObject<AutocompleteHandle | null>;
  onAutocompleteOpen?: () => void;
}

const DesktopFilterCategories: FunctionComponent<DesktopFilterCategoriesProps> = ({
  intl,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isAllFiltersModalOpen,
  setIsAllFiltersModalOpen,
  allFiltersModalSection,
  clearSearch,
  user,
  closeAutocomplete,
  autocompleteRef,
  onAutocompleteOpen,
}) => {
  const formatMessage = useFormatMessage();
  const { filters } = useSearchFilters();
  const menuAvatarSrc = useEffectiveUserProfilePhoto(user ?? null);
  const pageContext = usePageContext();
  const pathname = (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const locale = normalizeSupportedLocale(pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en');

  const filtersObj = (typeof filters === 'object' && filters) ? filters as Record<string, string[]> : {};
  // A facet excluded via the SRP negate-filter UI is a real, results-changing filter
  // (stored as `exclude_<attribute>`) and must count as "active" here too, or the location
  // button misleadingly shows inactive while an exclusion is applied.
  const isExcluded = (key: string) => !!filtersObj[`exclude_${key}`]?.length;
  const locationHasFilters = !!(filtersObj.location?.length || filtersObj.remoteness?.length || isExcluded('location'));
  const locationHasExcluded = isExcluded('location');

  const { query } = useSearchQuery();
  const { facetsData } = useFacets();
  const locationItems = facetsData.location || [];

  // Filter location facets to only show Finland, Remote, or Global locations (same allow/deny
  // list TopFilters and the location filter modal already use: kept local to this component
  // since it's the one place that needs the *filtered* list, not the raw facet response).
  const filterLocationFacets = useCallback((items: Array<{ value: string; label: string; count: number }>) => {
    const allowedKeywords = [
      'finland', 'suomi', 'finländ',
      'remote', 'etätyö', 'etä', 'distans', 'fjärr',
      'global', 'maailmanlaajuinen', 'världsomfattande',
    ];
    const excludedPatterns = [
      'remote - united states', 'etätyö - yhdysvallat', 'etä - yhdysvallat',
      'distans - usa', 'fjärr - usa', 'distans - förenta staterna', 'fjärr - förenta staterna',
    ];
    const activeLocationValues = filtersObj.location || [];
    return items.filter((item) => {
      if (activeLocationValues.includes(item.value)) return true;
      const lowerValue = item.value.toLowerCase();
      const lowerLabel = item.label.toLowerCase();
      if (excludedPatterns.some((pattern) => lowerValue.includes(pattern) || lowerLabel.includes(pattern))) return false;
      return allowedKeywords.some((keyword) => lowerValue.includes(keyword) || lowerLabel.includes(keyword));
    });
  }, [filtersObj.location]);
  const filteredLocationItems = filterLocationFacets(locationItems);

  // Opens the Advanced Filters modal straight to its Location tab, rather than the standalone
  // location popover: the modal is now the single place location filtering lives, this button is
  // just a shortcut into it. AllFiltersModal reads its initial tab from the same localStorage key
  // it already persists "last section shown" under, so writing it here before opening is enough.
  const handleLocationClick = useCallback(() => {
    closeAutocomplete?.();
    if (typeof window !== 'undefined') {
      localStorage.setItem('allFiltersModal.openSection', 'location');
    }
    setIsAllFiltersModalOpen?.(true);
  }, [closeAutocomplete, setIsAllFiltersModalOpen]);

  return (
    <header className={CORE_HEADER_STICKY_SRP} role="banner">
      {/* Top row: Logo + Searchbar (as wide as possible) + Advanced Filters + Hamburger Menu (lg and above) */}
      <nav className={`hidden lg:flex items-center gap-3 py-3 px-4 xl:px-20 md:px-10 ${CORE_NAV_ROW_BORDER}`} aria-label="Main navigation">
        {/* Left side: Logo + Searchbar. The search input itself has no max-width here (removed
            from SearchNavbar): with the category-button row gone, it fills the space instead
            of stopping short of it. The location picker lives inside SearchNavbar, attached to
            the search bar's right edge, rather than as a separate button in this row. */}
        <SearchNavbar
          intl={intl}
          formatMessage={formatMessage}
          clearSearch={clearSearch}
          autocompleteRef={autocompleteRef}
          onAutocompleteOpen={onAutocompleteOpen}
          onLocationClick={handleLocationClick}
          isLocationActive={!!isAllFiltersModalOpen && allFiltersModalSection === 'location'}
          locationHasFilters={locationHasFilters}
          locationHasExcluded={locationHasExcluded}
        />

        {/* Advanced Filters: opens the full filter modal (same one mobile's bottom nav opens):
            everything except location/remoteness, which the search bar's own location picker
            handles directly. */}
        <button
          type="button"
          onClick={() => {
            closeAutocomplete?.();
            setIsAllFiltersModalOpen?.(true);
          }}
          className={`flex items-center justify-center xl:justify-start gap-2 h-[52px] min-w-[52px] px-3 xl:px-4 rounded-full text-sm font-medium transition-all duration-300 ease-out cursor-pointer select-none border flex-shrink-0 bg-white/90 dark:bg-[#1F1509]/90 text-[#1E1A14] dark:text-[#F2EADF] border-[#DDD5C8]/70 dark:border-[#4A3520]/80 shadow-sm dark:shadow-none hover:shadow-md dark:hover:shadow-none hover:scale-[1.02] active:scale-[0.98]`}
          aria-label={formatMessage({
            id: 'search.refinements.advancedFilters.aria',
            defaultMessage: 'Advanced Filters',
            description: 'Accessibility label for the button that opens the full filters modal',
          })}
        >
          <Icon name="funnel" useMaskForColor className="w-4 h-4" />
          <span className="hidden xl:inline whitespace-nowrap font-semibold">
            {formatMessage({
              id: 'search.refinements.advancedFilters',
              defaultMessage: 'Advanced Filters',
              description: 'Label for the button that opens the full filters modal',
            })}
          </span>
        </button>

        {/* Right side: My Jobs, language, menu */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {/* My Jobs - always visible, same as NavigationNavbar on non-search pages */}
          <div className="hidden sm:flex items-center h-[52px]">
            <MyJobsNavEntry locale={locale} />
          </div>
          {/* Language Selector - FI/SV/EN side by side, only visible on xl screens and above.
              White track matching the row's other pills; each language is a true circle (never
              narrower than tall); the selected one gets its national flag as a 30%-opacity
              background tint instead of a plain highlight. */}
          <div className="hidden xl:flex items-center gap-1 h-[52px] px-1.5 rounded-full border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 bg-white/90 dark:bg-[#1F1509]/90">
            {(['fi', 'sv', 'en'] as const).map((lang) => {
              const isActive = locale === lang;
              const Flag = LANG_FLAGS[lang];
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => {
                    trackEvent({
                      name: 'locale_change',
                      params: {
                        to_locale: lang,
                        from_locale: locale,
                        surface: 'other',
                      },
                    });
                    window.location.href = convertUrlToLanguage(window.location.pathname, lang, intl);
                  }}
                  aria-current={isActive ? 'true' : undefined}
                  aria-label={formatMessage({ id: 'navigation.language', defaultMessage: 'Language', description: 'Button aria-label for language selector in navigation' })}
                  className="relative w-9 h-9 shrink-0 rounded-full overflow-hidden flex items-center justify-center text-xs font-extrabold uppercase cursor-pointer transition-colors"
                >
                  {isActive && <span className="absolute inset-0 opacity-30"><Flag /></span>}
                  <span
                    className={`relative ${
                      isActive
                        ? 'text-[#1E1A14] dark:text-[#F2EADF] [text-shadow:0_0_4px_white,0_0_4px_white,0_0_6px_white] dark:[text-shadow:0_0_4px_#1F1509,0_0_4px_#1F1509,0_0_6px_#1F1509]'
                        : 'text-jc-ink-secondary hover:text-jc-ink'
                    }`}
                  >
                    {lang.toUpperCase()}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            className={`flex items-center justify-center gap-2 h-[52px] min-w-[52px] rounded-full cursor-pointer hover:scale-[1.02] active:scale-[0.97] transition-[background-color,color,transform] duration-150 ${CORE_CONTROL_GHOST} ${
              user && menuAvatarSrc ? 'pl-1 pr-2' : 'px-2'
            }`}
            onClick={() => {
              const willOpen = !(isMobileMenuOpen ?? false);
              setIsMobileMenuOpen?.(willOpen);
              // Close All Filters modal if opening mobile menu
              if (willOpen && isAllFiltersModalOpen) {
                setIsAllFiltersModalOpen?.(false);
              }
            }}
            aria-label={formatMessage({
              id: 'navigation.menu',
              defaultMessage: 'Menu',
            })}
          >
            {user && menuAvatarSrc ? (
              <>
                <div className="flex-shrink-0 overflow-hidden rounded-full">
                  <Avatar
                    src={menuAvatarSrc}
                    size={32}
                    name={user.name || 'User'}
                  />
                </div>
                <List className="w-5 h-5 text-[#3D3529] dark:text-[#C4A882] flex-shrink-0" />
              </>
            ) : (
              <List className="w-6 h-6 text-[#3D3529] dark:text-[#C4A882]" />
            )}
          </button>
        </div>
      </nav>

      {/* Second row: suggested + selected facet pills (curated defaults, see TopFilters.tsx). */}
      <TopFilters
        intl={intl}
        formatMessage={formatMessage}
        clearSearch={clearSearch}
        facetsData={{
          ...facetsData,
          location: filteredLocationItems,
        }}
        filtersObj={filtersObj}
        query={query}
      />
    </header>
  );
};

// Memoize DesktopFilterCategories to prevent unnecessary re-renders
export const DesktopFilterCategoriesMemo = memo(DesktopFilterCategories);
